import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  fullName: z.string().trim().min(3, "Por favor, insira seu nome completo").max(100),
  email: z.string().trim().email("E-mail inválido").max(255),
  whatsapp: z.string().trim().min(10, "WhatsApp inválido (inclua o DDD)").max(20),
  motivations: z.array(z.string()).min(1, "Selecione pelo menos uma motivação"),
  realEstateWealth: z.string().min(1, "Selecione uma opção"),
  financialWealth: z.string().min(1, "Selecione uma opção"),
  monthlyIncome: z.string().min(1, "Selecione uma opção"),
  expectations: z.string().trim().min(10, "Por favor, compartilhe suas expectativas").max(500),
});

type FormData = z.infer<typeof formSchema>;

const motivationOptions = [
  { id: "organize", label: "Organizar melhor minhas decisões financeiras" },
  { id: "professionalize", label: "Profissionalizar meus investimentos" },
  { id: "taxes", label: "Ter mais clareza sobre impostos e estrutura patrimonial" },
  { id: "longterm", label: "Planejar o longo prazo com mais segurança" },
  { id: "succession", label: "Preparar sucessão ou proteção do patrimônio" },
  { id: "other", label: "Outro" },
];

const realEstateOptions = [
  { value: "none", label: "Não possuo patrimônio imobiliário" },
  { value: "up-to-500k", label: "Até R$500 mil" },
  { value: "500k-2m", label: "Entre R$500 mil e R$2 milhões" },
  { value: "2m-5m", label: "Entre R$2 milhões e R$5 milhões" },
  { value: "above-5m", label: "Acima de R$5 milhões" },
];

const financialWealthOptions = [
  { value: "up-to-250k", label: "Até R$250 mil" },
  { value: "250k-1m", label: "Entre R$250 mil e R$1 milhão" },
  { value: "1m-5m", label: "Entre R$1 milhão e R$5 milhões" },
  { value: "above-5m", label: "Acima de R$5 milhões" },
];

const monthlyIncomeOptions = [
  { value: "up-to-7k", label: "Até R$7 mil" },
  { value: "7k-15k", label: "Entre R$7 mil e R$15 mil" },
  { value: "15k-30k", label: "Entre R$15 mil e R$30 mil" },
  { value: "30k-60k", label: "Entre R$30 mil e R$60 mil" },
  { value: "above-60k", label: "Acima de R$60 mil" },
];

const TOTAL_STEPS = 6;

const DiagnosticForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      whatsapp: "",
      motivations: [],
      realEstateWealth: "",
      financialWealth: "",
      monthlyIncome: "",
      expectations: "",
    },
  });

  const selectedMotivations = watch("motivations");
  const realEstateWealth = watch("realEstateWealth");
  const financialWealth = watch("financialWealth");
  const monthlyIncome = watch("monthlyIncome");

  const handleMotivationChange = (motivationId: string, checked: boolean) => {
    const current = selectedMotivations || [];
    if (checked) {
      setValue("motivations", [...current, motivationId]);
    } else {
      setValue("motivations", current.filter((m) => m !== motivationId));
    }
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    switch (currentStep) {
      case 1:
        return await trigger(["fullName", "email", "whatsapp"]);
      case 2:
        return await trigger("motivations");
      case 3:
        return await trigger("realEstateWealth");
      case 4:
        return await trigger("financialWealth");
      case 5:
        return await trigger("monthlyIncome");
      case 6:
        return await trigger("expectations");
      default:
        return true;
    }
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.from("leads").insert({
        full_name: data.fullName,
        email: data.email,
        whatsapp: data.whatsapp,
        motivations: data.motivations,
        real_estate_wealth: data.realEstateWealth,
        financial_wealth: data.financialWealth,
        monthly_income: data.monthlyIncome,
        expectations: data.expectations,
      });

      if (error) {
        console.error("Error saving lead:", error);
        toast({
          title: "Erro ao enviar",
          description: "Ocorreu um erro ao enviar sua solicitação. Tente novamente.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      toast({
        title: "Solicitação enviada",
        description: "Sua solicitação foi recebida com sucesso.",
      });
      
      if (data.monthlyIncome === "up-to-7k") {
        navigate("/obrigado-alt");
      } else {
        navigate("/obrigado");
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      toast({
        title: "Erro ao enviar",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-1.5 mb-8">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((step) => (
        <div
          key={step}
          className={cn(
            "h-2.5 rounded-full transition-all duration-300",
            step === currentStep
              ? "w-6 bg-[#F3DE2C]"
              : step < currentStep
              ? "w-2.5 bg-[#112711]"
              : "w-2.5 bg-[#112711]/40"
          )}
        />
      ))}
    </div>
  );

  const NavigationButtons = ({ showSubmit = false }: { showSubmit?: boolean }) => (
    <div className="flex items-center justify-end mt-6 pt-4">
      {currentStep > 1 && (
        <button
          type="button"
          onClick={handlePrev}
          className="flex items-center gap-1.5 text-xs font-medium text-[#FAFAFA]/70 hover:text-[#FAFAFA] transition-colors mr-auto"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Voltar
        </button>
      )}

      {showSubmit ? (
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center gap-1.5 bg-[#F3DE2C] text-[#112711] px-5 py-2.5 rounded-full text-xs font-medium transition-all hover:bg-[#F3DE2C]/90 disabled:opacity-50"
        >
          {isSubmitting ? (
            "Enviando..."
          ) : (
            <>
              Enviar solicitação
              <Check className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleNext}
          className="flex items-center gap-1.5 bg-[#F3DE2C] text-[#112711] px-5 py-2.5 rounded-full text-xs font-medium transition-all hover:bg-[#F3DE2C]/90"
        >
          Continuar
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  // Custom input styles
  const inputClasses = "w-full bg-transparent border border-[#FAFAFA]/30 rounded-lg px-3 py-2.5 text-sm text-[#FAFAFA] placeholder:text-[#FAFAFA]/40 focus:outline-none focus:border-[#F3DE2C] transition-colors";
  const labelClasses = "block text-[#FAFAFA] font-medium text-sm mb-1.5";
  const errorClasses = "text-red-400 text-xs mt-1";

  return (
    <section id="form-section" className="py-16 md:py-20 bg-[#F5F3EE]">
      <div className="section-container">
        <div className="max-w-xl mx-auto">
          {/* Title */}
          <h2 className="text-xl md:text-2xl lg:text-3xl font-display font-normal mb-4 text-[#112711] text-center">
            Solicite seu convite de diagnóstico patrimonial.
          </h2>
          
          {/* Intro text */}
          <p className="text-sm text-[#112711]/70 font-body text-center mb-2 leading-relaxed">
            Se você sente que essa conversa pode fazer sentido para o seu momento, preencha o formulário abaixo.
          </p>
          <p className="text-xs text-[#112711]/60 font-body text-center mb-8">
            As solicitações passam por uma breve análise de perfil.
          </p>
          
          <StepIndicator />
          
          {/* Form Card */}
          <form onSubmit={handleSubmit(onSubmit)} className="bg-[#112711] rounded-xl p-6 md:p-8">
            {/* Step 1 - Contact fields */}
            <div className={cn("space-y-4", currentStep !== 1 && "hidden")}>
              <div>
                <label htmlFor="fullName" className={labelClasses}>
                  Nome completo
                </label>
                <input
                  id="fullName"
                  placeholder="Seu nome completo"
                  {...register("fullName")}
                  className={inputClasses}
                />
                {errors.fullName && (
                  <p className={errorClasses}>{errors.fullName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className={labelClasses}>
                  E-mail
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  {...register("email")}
                  className={inputClasses}
                />
                {errors.email && (
                  <p className={errorClasses}>{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="whatsapp" className={labelClasses}>
                  WhatsApp (com DDD)
                </label>
                <input
                  id="whatsapp"
                  placeholder="(11) 99999-9999"
                  {...register("whatsapp")}
                  className={inputClasses}
                />
                <p className="text-[10px] text-[#FAFAFA]/50 mt-1">
                  Usaremos apenas para entrar em contato sobre sua solicitação.
                </p>
                {errors.whatsapp && (
                  <p className={errorClasses}>{errors.whatsapp.message}</p>
                )}
              </div>

              <NavigationButtons />
            </div>

            {/* Step 2 - Motivations */}
            <div className={cn("space-y-4", currentStep !== 2 && "hidden")}>
              <div>
                <p className={labelClasses}>
                  O que mais te motivou a solicitar esse diagnóstico?
                </p>
                <p className="text-xs text-[#FAFAFA]/50 mb-3">Selecione todas que se aplicam</p>
              </div>
              
              <div className="space-y-2.5">
                {motivationOptions.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-center gap-2.5 cursor-pointer group"
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded border-2 flex items-center justify-center transition-all",
                        selectedMotivations?.includes(option.id)
                          ? "bg-[#F3DE2C] border-[#F3DE2C]"
                          : "border-[#FAFAFA]/40 group-hover:border-[#FAFAFA]/60"
                      )}
                      onClick={() => handleMotivationChange(option.id, !selectedMotivations?.includes(option.id))}
                    >
                      {selectedMotivations?.includes(option.id) && (
                        <Check className="w-2.5 h-2.5 text-[#112711]" strokeWidth={3} />
                      )}
                    </div>
                    <span className="text-sm text-[#FAFAFA]/90">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.motivations && (
                <p className={errorClasses}>{errors.motivations.message}</p>
              )}

              <NavigationButtons />
            </div>

            {/* Step 3 - Real Estate Wealth */}
            <div className={cn("space-y-4", currentStep !== 3 && "hidden")}>
              <p className={labelClasses}>
                Considerando seu patrimônio imobiliário aproximado (ex: imóveis residenciais, comerciais, terrenos), em qual faixa você se encontra?
              </p>
              
              <div className="space-y-2.5">
                {realEstateOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2.5 cursor-pointer group"
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                        realEstateWealth === option.value
                          ? "border-[#F3DE2C]"
                          : "border-[#FAFAFA]/40 group-hover:border-[#FAFAFA]/60"
                      )}
                      onClick={() => setValue("realEstateWealth", option.value)}
                    >
                      {realEstateWealth === option.value && (
                        <div className="w-2 h-2 rounded-full bg-[#F3DE2C]" />
                      )}
                    </div>
                    <span className="text-sm text-[#FAFAFA]/90">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.realEstateWealth && (
                <p className={errorClasses}>{errors.realEstateWealth.message}</p>
              )}

              <NavigationButtons />
            </div>

            {/* Step 4 - Financial Wealth */}
            <div className={cn("space-y-4", currentStep !== 4 && "hidden")}>
              <p className={labelClasses}>
                Considerando seu patrimônio em aplicações financeiras (ex: renda fixa, fundos, ações, previdência), em qual faixa você se encontra?
              </p>
              
              <div className="space-y-2.5">
                {financialWealthOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2.5 cursor-pointer group"
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                        financialWealth === option.value
                          ? "border-[#F3DE2C]"
                          : "border-[#FAFAFA]/40 group-hover:border-[#FAFAFA]/60"
                      )}
                      onClick={() => setValue("financialWealth", option.value)}
                    >
                      {financialWealth === option.value && (
                        <div className="w-2 h-2 rounded-full bg-[#F3DE2C]" />
                      )}
                    </div>
                    <span className="text-sm text-[#FAFAFA]/90">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.financialWealth && (
                <p className={errorClasses}>{errors.financialWealth.message}</p>
              )}

              <NavigationButtons />
            </div>

            {/* Step 5 - Monthly Income */}
            <div className={cn("space-y-4", currentStep !== 5 && "hidden")}>
              <p className={labelClasses}>
                Sua renda mensal aproximada hoje está em qual faixa?
              </p>
              
              <div className="space-y-2.5">
                {monthlyIncomeOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2.5 cursor-pointer group"
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                        monthlyIncome === option.value
                          ? "border-[#F3DE2C]"
                          : "border-[#FAFAFA]/40 group-hover:border-[#FAFAFA]/60"
                      )}
                      onClick={() => setValue("monthlyIncome", option.value)}
                    >
                      {monthlyIncome === option.value && (
                        <div className="w-2 h-2 rounded-full bg-[#F3DE2C]" />
                      )}
                    </div>
                    <span className="text-sm text-[#FAFAFA]/90">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.monthlyIncome && (
                <p className={errorClasses}>{errors.monthlyIncome.message}</p>
              )}

              <NavigationButtons />
            </div>

            {/* Step 6 - Expectations */}
            <div className={cn("space-y-4", currentStep !== 6 && "hidden")}>
              <label htmlFor="expectations" className={labelClasses}>
                O que você espera obter dessa conversa?
              </label>
              
              <textarea
                id="expectations"
                placeholder="Compartilhe suas expectativas..."
                {...register("expectations")}
                className={cn(inputClasses, "min-h-[100px] resize-none")}
              />
              {errors.expectations && (
                <p className={errorClasses}>{errors.expectations.message}</p>
              )}

              {/* Closing text */}
              <p className="text-xs text-[#FAFAFA]/60 font-body">
                Analisamos todas as solicitações individualmente.
                <br />
                Caso haja alinhamento, entraremos em contato para dar continuidade à conversa.
              </p>

              <NavigationButtons showSubmit />
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default DiagnosticForm;
